import * as yup from 'yup';

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { AnnualReportNavigationDirection, AnnualReportProps } from '../AnnualReport';
import { DegreeTypes, CreditTypes, Days, Months, SatelliteType } from '../../../common/Data';
import { Button, Input, SatelliteForm } from '../../../components';
import {
    Card,
    CardBody,
    CardFooter,
    Col,
    Form,
    FormGroup,
    InputGroup,
    InputGroupText,
    Label,
    Modal,
    ModalBody,
    ModalHeader,
    Row,
} from 'reactstrap';
import {
    ProgramInformation as ProgramInfoDto,
    ProgramInformationDetail,
    Satellite,
    TabSetting,
} from '../../../common/Types';

interface ProgramInformationFormData {
    programInfoId: number;
    programId: number;
    activeYear: number;
    certificateAwarded: string;
    typeOfAward: string;
    lengthOfProgram: number;
    lengthOfProgramAcademicSession: number;
    totalCreditHoursRequired: number;
    typeOfCredits: string;
    totalFeesResident: number;
    totalFeesNonResident: number;
    typeOfAward2?: string;
    lengthOfProgram2?: number;
    lengthOfProgramAcademicSession2?: number;
    totalCreditHoursRequired2?: number;
    typeOfCredits2?: string;
    totalFeesResident2?: number;
    totalFeesNonResident2?: number;
    programFiscalYearBeginsOnMonth: number;
    programFiscalYearBeginsOnDate: number;
    totalExpenditures?: number;
    isBudgetSufficient: boolean;
    detailedAnalysisOfBudget?: string;
    actionPlanForBudget?: string;
    createdBy?: string;
    dateCreated: Date | null;
    lastEdited: Date | null;
    editedBy?: string;
    totalPreviousExpenditures?: number;
    createdByString?: string;
    editedByString?: string;
    programInformationDetail: ProgramInformationDetail[];
    website: string;
}

import Skeleton from 'react-loading-skeleton';
import { professionService } from '../../../services/ProfessionAdministration';
import { programService } from '../../../services/ProgramService';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

const FlexFormGroup = styled(FormGroup)`
    display: flex;
    flex-direction: column;
    align-items: stretch;
`;

const ProgramInformation = (props: AnnualReportProps) => {
    const [programInformation, setProgramInformation] = useState<ProgramInfoDto>();
    const [formSubmitting, setFormSubmitting] = useState<boolean>(false);
    const [professionExpenditures, setProfessionExpenditures] = useState<TabSetting[]>([]);
    const [submitDirection, setSubmitDirection] = useState<AnnualReportNavigationDirection>(
        AnnualReportNavigationDirection.Forward,
    );

    // Memoize validation schema
    const validationSchema = useMemo(
        () =>
            yup.object({
                programInfoId: yup
                    .number()
                    .required('This field is required')
                    .default(programInformation?.programInfoId || 0),
                programId: yup.number().required('This field is required').default(props.program.programId),
                activeYear: yup.number().required('This field is required').default(+props.reportYear),
                certificateAwarded: yup.string().required('This field is required'),
                typeOfAward: yup.string().required('This field is required'),
                lengthOfProgram: yup
                    .number()
                    .required('This field is required')
                    .typeError('This field is required and must be a number'),
                lengthOfProgramAcademicSession: yup
                    .number()
                    .required('This field is required')
                    .typeError('This field is required and must be a number'),
                totalCreditHoursRequired: yup
                    .number()
                    .required('This field is required')
                    .typeError('This field is required and must be a number'),
                typeOfCredits: yup.string().required('This field is required'),
                totalFeesResident: yup
                    .number()
                    .required('This field is required')
                    .typeError('This field is required and must be a number'),
                totalFeesNonResident: yup
                    .number()
                    .required('This field is required')
                    .typeError('This field is required and must be a number'),
                typeOfAward2: yup.string().optional().nullable(),
                lengthOfProgram2: yup
                    .number()
                    .notRequired()
                    .optional()
                    .nullable()
                    .transform((val, org) => {
                        return val === null || val === undefined || val === '' ? undefined : val;
                    })
                    .typeError('This field must be a number'),
                lengthOfProgramAcademicSession2: yup
                    .number()
                    .notRequired()
                    .optional()
                    .nullable()
                    .transform((val, org) => {
                        return val === null || val === undefined || val === '' ? undefined : val;
                    })
                    .typeError('This field must be a number'),
                totalCreditHoursRequired2: yup
                    .number()
                    .notRequired()
                    .optional()
                    .nullable()
                    .transform((val, org) => {
                        return val === null || val === undefined || val === '' ? undefined : val;
                    })
                    .typeError('This field must be a number'),
                typeOfCredits2: yup.string().optional().nullable(),
                totalFeesResident2: yup
                    .number()
                    .notRequired()
                    .optional()
                    .nullable()
                    .transform((val, org) => {
                        return val === null || val === undefined || val === '' ? undefined : val;
                    })
                    .typeError('This field must be a number'),
                totalFeesNonResident2: yup
                    .number()
                    .notRequired()
                    .optional()
                    .nullable()
                    .transform((val, org) => {
                        return val === null || val === undefined || val === '' ? undefined : val;
                    })
                    .typeError('This field must be a number'),
                programFiscalYearBeginsOnMonth: yup
                    .number()
                    .required('This field is required')
                    .typeError('This field is required'),
                programFiscalYearBeginsOnDate: yup
                    .number()
                    .required('This field is required')
                    .typeError('This field is required'),
                totalExpenditures: yup.number().optional().nullable(),
                isBudgetSufficient: yup
                    .boolean()
                    .typeError('Please select Yes or No.')
                    .required('Please select Yes or No.'),
                detailedAnalysisOfBudget: yup
                    .string()
                    .when('isBudgetSufficient', {
                        is: false,
                        then: (schema) => schema.required('This field is required'),
                        otherwise: (schema) => schema.notRequired(),
                    }),
                actionPlanForBudget: yup
                    .string()
                    .when('isBudgetSufficient', {
                        is: false,
                        then: (schema) => schema.required('This field is required'),
                        otherwise: (schema) => schema.notRequired(),
                    }),
                createdBy: yup.string().optional().nullable(),
                dateCreated: yup.date().notRequired().nullable(),
                lastEdited: yup.date().notRequired().nullable(),
                editedBy: yup.string().optional().nullable(),
                totalPreviousExpenditures: yup.number().optional().nullable(),
                createdByString: yup.string().optional().nullable(),
                editedByString: yup.string().optional().nullable(),
                programInformationDetail: yup
                    .array()
                    .of(
                        yup
                            .object<ProgramInformationDetail>({
                                id: yup.number().required().default(0),
                                programInfoId: yup.number().required().default(0),
                                title: yup.string().required('This field is required'),
                                expenditure: yup
                                    .number()
                                    .required('This field is required')
                                    .typeError('This field is required and must be a number'),
                                previousExpenditure: yup
                                    .number()
                                    .required('This field is required')
                                    .typeError('This field is required and must be a number'),
                            })
                            .required(),
                    )
                    .notRequired(),
                website: yup
                    .string()
                    .required('This field is required')
                    .max(500, 'URL cannot exceed 500 characters')
                    .matches(/^https?:\/\/.+/, 'URL must start with http:// or https://'),
            }),
        [programInformation, props.program.programId, props.reportYear],
    );

    // Memoize default values
    const defaultValues = useMemo((): ProgramInformationFormData => {
        if (!programInformation) {
            return {
                programInfoId: 0,
                programId: props.program.programId,
                activeYear: +props.reportYear,
                certificateAwarded: '',
                typeOfAward: '',
                lengthOfProgram: 0,
                lengthOfProgramAcademicSession: 0,
                totalCreditHoursRequired: 0,
                typeOfCredits: '',
                totalFeesResident: 0,
                totalFeesNonResident: 0,
                typeOfAward2: undefined,
                lengthOfProgram2: undefined,
                lengthOfProgramAcademicSession2: undefined,
                totalCreditHoursRequired2: undefined,
                typeOfCredits2: undefined,
                totalFeesResident2: undefined,
                totalFeesNonResident2: undefined,
                programFiscalYearBeginsOnMonth: 0,
                programFiscalYearBeginsOnDate: 0,
                totalExpenditures: undefined,
                isBudgetSufficient: false,
                detailedAnalysisOfBudget: undefined,
                actionPlanForBudget: undefined,
                createdBy: undefined,
                dateCreated: null,
                lastEdited: null,
                editedBy: undefined,
                totalPreviousExpenditures: undefined,
                createdByString: undefined,
                editedByString: undefined,
                programInformationDetail: [],
                website: '',
            };
        }
        return {
            ...programInformation,
            programInformationDetail: programInformation.programInformationDetail || [],
            isBudgetSufficient: programInformation.isBudgetSufficient ?? false,
            dateCreated: programInformation.dateCreated ?? null,
            lastEdited: programInformation.lastEdited ?? null,
        };
    }, [programInformation, props.program.programId, props.reportYear]);

    const {
        control,
        handleSubmit,
        register,
        reset,
        watch,
        getValues,
        setValue,
        formState: { errors, isDirty },
    } = useForm<ProgramInformationFormData>({
        resolver: yupResolver(validationSchema) as any,
        mode: 'onChange',
        defaultValues,
    });

    const [satellites, setSatellites] = useState<Satellite[]>([]);
    const [selectedSatellite, setSelectedSatellite] = useState<Satellite>();
    const [reloadSatellites, setReloadSatellites] = useState<boolean>(true);
    const [showModal, setModal] = useState(false);

    const toggleModal = () => setModal(!showModal);

    const watchBudget = watch('isBudgetSufficient');
    const watchDetail = watch('programInformationDetail');

    useEffect(() => {
        programService
            .getProgramInformationByProgramIdAndYear(props.program.programId, props.reportYear)
            .then((result) => {
                setProgramInformation(result);
                reset({
                    ...result,
                });
            })
            .catch(() => {
                toast.error('Error loading program information');
            });

        professionService
            .getProgramInformation(props.profession.professionId)
            .then((results) => {
                setProfessionExpenditures(results);
            })
            .catch(() => {
                toast.error('Error loading expenditures');
            });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.program.programId, props.reportYear, props.profession.professionId]);

    useEffect(() => {
        if (reloadSatellites) {
            programService
                .getSatelliteCampusesByProgramId(props.program.programId)
                .then((results) => {
                    setSatellites([...results.filter((s) => s.satalliteTypeId === SatelliteType['Satellite Campus'])]);
                    setReloadSatellites(false);
                })
                .catch(() => {
                    toast.error('Error getting satellites.');
                });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reloadSatellites, props.program.programId]);

    const findExpenditureInDetail = (expenditureTitle: string): ProgramInformationDetail | undefined => {
        if (programInformation && programInformation.programInformationDetail) {
            return programInformation.programInformationDetail.find((d) => d.title === expenditureTitle);
        }

        return undefined;
    };

    const getExpTotal = (): number | undefined => {
        if (watchDetail && watchDetail.length > 0) {
            let totalExp = 0;
            let expHasVal = false;

            watchDetail.forEach((detail) => {
                if (detail.expenditure) {
                    expHasVal = true;
                    totalExp += +detail.expenditure;
                }
            });

            return expHasVal ? totalExp : undefined;
        }

        return undefined;
    };

    const getPrevExpTotal = (): number | undefined => {
        if (watchDetail && watchDetail.length > 0) {
            let totalPrevExp = 0;
            let prevExpHasVal = false;

            watchDetail.forEach((detail) => {
                if (detail.previousExpenditure) {
                    prevExpHasVal = true;
                    totalPrevExp += +detail.previousExpenditure;
                }
            });

            return prevExpHasVal ? totalPrevExp : undefined;
        }

        return undefined;
    };

    const submitForm = useCallback(
        async (values: ProgramInformationFormData) => {
            if (!isDirty && values.programInfoId > 0) {
                if (submitDirection === AnnualReportNavigationDirection.Forward) {
                    props.onSavedSuccessfully(AnnualReportNavigationDirection.Forward);
                }
                return;
            }

            setFormSubmitting(true);
            const toastId = toast.info('Saving Program Information...');

            try {
                const result = await programService.saveProgramInformation(values as ProgramInfoDto);
                toast.update(toastId, {
                    type: 'success',
                    render: 'Program information successfully saved',
                });

                setProgramInformation(result);
                reset(result);

                if (submitDirection === AnnualReportNavigationDirection.Forward) {
                    props.onSavedSuccessfully(AnnualReportNavigationDirection.Forward);
                }
            } catch (error) {
                toast.update(toastId, {
                    type: 'error',
                    render: 'Error saving program information',
                });
            } finally {
                setFormSubmitting(false);
            }
        },
        [isDirty, submitDirection, props, reset],
    );

    const submitWithoutValidation = useCallback(async () => {
        setFormSubmitting(true);
        const toastId = toast.info('Saving Program Information...');

        try {
            const values = getValues();
            const result = await programService.saveProgramInformation(values as ProgramInfoDto);

            toast.update(toastId, {
                type: 'success',
                render: 'Program information successfully saved',
            });

            setProgramInformation(result);
            reset(result);
        } catch (error) {
            toast.update(toastId, {
                type: 'error',
                render: 'Error saving program information',
            });
        } finally {
            setFormSubmitting(false);
        }
    }, [getValues, reset]);

    const handleSatelliteSave = useCallback(() => {
        setReloadSatellites(true);
        setSelectedSatellite(undefined);
        setModal(false);
    }, []);

    const selectSatellite = useCallback((satellite: Satellite) => {
        setSelectedSatellite(satellite);
        toggleModal();
    }, []);

    const newSatellite = useCallback(() => {
        setSelectedSatellite({
            isActive: true,
            programId: +props.program.programId,
            satelliteId: 0,
        });
        toggleModal();
    }, [props.program.programId]);

    return (
        <>
            <Row>
                <Col>
                    <h4 className={'card-title'}>Program Information</h4>
                </Col>
            </Row>
            <Row>
                <Col>
                    {programInformation && professionExpenditures && (
                        <>
                            <Form noValidate onSubmit={handleSubmit(submitForm, (validationErrors) => {
                                const fieldNames = Object.keys(validationErrors);
                                toast.error(`Please fix the following fields: ${fieldNames.join(', ')}`);
                            })}>
                                <Card>
                                    <CardBody>
                                        <h5
                                            className={'card-subtitle text-muted mb-2'}
                                        >{`* denotes a required field`}</h5>
                                        <input type="hidden" {...register('programInfoId')} />
                                        <input type="hidden" {...register('programId')} />
                                        <input type="hidden" {...register('activeYear')} />
                                        <input type="hidden" {...register('dateCreated')} />
                                        <input type="hidden" {...register('createdByString')} />
                                        <input type="hidden" {...register('editedByString')} />
                                        <input type="hidden" {...register('lastEdited')} />
                                        <FlexFormGroup>
                                            <Label>Program Title</Label>
                                            <span>{props.program.title}</span>
                                        </FlexFormGroup>
                                        <FlexFormGroup>
                                            <Label>
                                                Name of certificate or degree awarded
                                                <span className={'text-danger ms-1'}>*</span>
                                            </Label>
                                            <Controller
                                                name="certificateAwarded"
                                                control={control}
                                                render={({ field }) => (
                                                    <Input {...field} type={'text'} />
                                                )}
                                            />
                                            {errors?.certificateAwarded?.message && (
                                                <Label className={'text-danger'}>
                                                    {errors.certificateAwarded.message}
                                                </Label>
                                            )}
                                        </FlexFormGroup>
                                        <FlexFormGroup>
                                            <Label>
                                                {`URL to program summary outcomes results required in Standard V.A.4.`}
                                                <span className={'text-danger ms-1'}>*</span>
                                            </Label>
                                            <Controller
                                                name="website"
                                                control={control}
                                                render={({ field }) => (
                                                    <Input {...field} type={'url'} placeholder={'https://...'} />
                                                )}
                                            />
                                            <div className={'d-flex justify-content-between mt-1'}>
                                                <div>
                                                    {errors?.website?.message && (
                                                        <Label className={'text-danger'}>
                                                            {errors.website.message}
                                                        </Label>
                                                    )}
                                                </div>
                                                <small className={'text-muted'}>
                                                    {watch('website')?.length || 0}/500 characters
                                                </small>
                                            </div>
                                        </FlexFormGroup>
                                        <Row className={'my-2'}>
                                            <Col>
                                                <h4 className={'card-title'}>Program Design</h4>
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col className={'d-flex flex-column'}>
                                                <h5 className={'card-subtitle mb-2'}>Award Level 1</h5>
                                                <FlexFormGroup>
                                                    <Label>
                                                        Type of award granted
                                                        <span className={'text-danger ms-1'}>*</span>
                                                    </Label>
                                                    <Controller
                                                        name="typeOfAward"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <select
                                                                className={'select form-control align-self-stretch'}
                                                                {...field}
                                                            >
                                                                <option value={''}>Select...</option>
                                                                {DegreeTypes.map((at) => (
                                                                    <option key={at} value={at}>
                                                                        {at}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        )}
                                                    />
                                                    {errors?.typeOfAward?.message && (
                                                        <Label className={'text-danger'}>
                                                            {errors.typeOfAward.message}
                                                        </Label>
                                                    )}
                                                </FlexFormGroup>
                                                <FlexFormGroup>
                                                    <Label>
                                                        Length of Program in Months
                                                        <span className={'text-danger ms-1'}>*</span>
                                                    </Label>
                                                    <Controller
                                                        name="lengthOfProgram"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Input
                                                                {...field}
                                                                type={'number'}
                                                                onChange={(e) => field.onChange(e.target.value === '' ? undefined : +e.target.value)}
                                                            />
                                                        )}
                                                    />
                                                    {errors?.lengthOfProgram?.message && (
                                                        <Label className={'text-danger'}>
                                                            {errors.lengthOfProgram.message}
                                                        </Label>
                                                    )}
                                                </FlexFormGroup>
                                                <FlexFormGroup>
                                                    <Label>
                                                        Length of Program in Academic Sessions
                                                        <span className={'text-danger ms-1'}>*</span>
                                                    </Label>
                                                    <Controller
                                                        name="lengthOfProgramAcademicSession"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Input
                                                                {...field}
                                                                type={'number'}
                                                                onChange={(e) => field.onChange(e.target.value === '' ? undefined : +e.target.value)}
                                                            />
                                                        )}
                                                    />
                                                    {errors?.lengthOfProgramAcademicSession?.message && (
                                                        <Label className={'text-danger'}>
                                                            {errors.lengthOfProgramAcademicSession.message}
                                                        </Label>
                                                    )}
                                                </FlexFormGroup>
                                                <FlexFormGroup>
                                                    <Label>
                                                        Total Credit Hours Required
                                                        <span className={'text-danger ms-1'}>*</span>
                                                    </Label>
                                                    <Controller
                                                        name="totalCreditHoursRequired"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Input
                                                                {...field}
                                                                type={'number'}
                                                                onChange={(e) => field.onChange(e.target.value === '' ? undefined : +e.target.value)}
                                                            />
                                                        )}
                                                    />
                                                    {errors?.totalCreditHoursRequired?.message && (
                                                        <Label className={'text-danger'}>
                                                            {errors.totalCreditHoursRequired.message}
                                                        </Label>
                                                    )}
                                                </FlexFormGroup>
                                                <FlexFormGroup>
                                                    <Label>
                                                        Type of Credits<span className={'text-danger ms-1'}>*</span>
                                                    </Label>
                                                    <Controller
                                                        name="typeOfCredits"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <select
                                                                className={'select form-control align-self-stretch'}
                                                                {...field}
                                                            >
                                                                <option value={''}>Select...</option>
                                                                {CreditTypes.map((ct) => (
                                                                    <option key={ct} value={ct}>
                                                                        {ct}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        )}
                                                    />
                                                    {errors?.typeOfCredits?.message && (
                                                        <Label className={'text-danger'}>
                                                            {errors.typeOfCredits.message}
                                                        </Label>
                                                    )}
                                                </FlexFormGroup>
                                                <FlexFormGroup>
                                                    <Label>
                                                        Total Program Tuition and Fees - Resident
                                                        <span className={'text-danger ms-1'}>*</span>
                                                    </Label>
                                                    <Controller
                                                        name="totalFeesResident"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Input
                                                                {...field}
                                                                type={'number'}
                                                                onChange={(e) => field.onChange(e.target.value === '' ? undefined : +e.target.value)}
                                                            />
                                                        )}
                                                    />
                                                    {errors?.totalFeesResident?.message && (
                                                        <Label className={'text-danger'}>
                                                            {errors.totalFeesResident.message}
                                                        </Label>
                                                    )}
                                                </FlexFormGroup>
                                                <FlexFormGroup>
                                                    <Label>
                                                        Total Program Tuition and Fees - Non-Resident
                                                        <span className={'text-danger ms-1'}>*</span>
                                                    </Label>
                                                    <Controller
                                                        name="totalFeesNonResident"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Input
                                                                {...field}
                                                                type={'number'}
                                                                onChange={(e) => field.onChange(e.target.value === '' ? undefined : +e.target.value)}
                                                            />
                                                        )}
                                                    />
                                                    {errors?.totalFeesNonResident?.message && (
                                                        <Label className={'text-danger'}>
                                                            {errors.totalFeesNonResident.message}
                                                        </Label>
                                                    )}
                                                </FlexFormGroup>
                                            </Col>
                                            <Col className={'d-flex flex-column'}>
                                                <h5 className={'card-subtitle mb-2'}>Award Level 2</h5>
                                                <FlexFormGroup>
                                                    <Label>Type of award granted</Label>
                                                    <Controller
                                                        name="typeOfAward2"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <select
                                                                className={'select form-control align-self-stretch'}
                                                                {...field}
                                                                value={field.value ?? ''}
                                                            >
                                                                <option value={''}>Select...</option>
                                                                {DegreeTypes.map((at) => (
                                                                    <option key={at} value={at}>
                                                                        {at}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        )}
                                                    />
                                                    {errors?.typeOfAward2?.message && (
                                                        <Label className={'text-danger'}>
                                                            {errors.typeOfAward2.message}
                                                        </Label>
                                                    )}
                                                </FlexFormGroup>
                                                <FlexFormGroup>
                                                    <Label>Length of Program in Months</Label>
                                                    <Controller
                                                        name="lengthOfProgram2"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Input
                                                                {...field}
                                                                type={'number'}
                                                                value={field.value ?? ''}
                                                                onChange={(e) => field.onChange(e.target.value === '' ? undefined : +e.target.value)}
                                                            />
                                                        )}
                                                    />
                                                    {errors?.lengthOfProgram2?.message && (
                                                        <Label className={'text-danger'}>
                                                            {errors.lengthOfProgram2.message}
                                                        </Label>
                                                    )}
                                                </FlexFormGroup>
                                                <FlexFormGroup>
                                                    <Label>Length of Program in Academic Sessions</Label>
                                                    <Controller
                                                        name="lengthOfProgramAcademicSession2"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Input
                                                                {...field}
                                                                type={'number'}
                                                                value={field.value ?? ''}
                                                                onChange={(e) => field.onChange(e.target.value === '' ? undefined : +e.target.value)}
                                                            />
                                                        )}
                                                    />
                                                    {errors?.lengthOfProgramAcademicSession2?.message && (
                                                        <Label className={'text-danger'}>
                                                            {errors.lengthOfProgramAcademicSession2.message}
                                                        </Label>
                                                    )}
                                                </FlexFormGroup>
                                                <FlexFormGroup>
                                                    <Label>Total Credit Hours Required</Label>
                                                    <Controller
                                                        name="totalCreditHoursRequired2"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Input
                                                                {...field}
                                                                type={'number'}
                                                                value={field.value ?? ''}
                                                                onChange={(e) => field.onChange(e.target.value === '' ? undefined : +e.target.value)}
                                                            />
                                                        )}
                                                    />
                                                    {errors?.totalCreditHoursRequired2?.message && (
                                                        <Label className={'text-danger'}>
                                                            {errors.totalCreditHoursRequired2.message}
                                                        </Label>
                                                    )}
                                                </FlexFormGroup>
                                                <FlexFormGroup>
                                                    <Label>Type of Credits</Label>
                                                    <Controller
                                                        name="typeOfCredits2"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <select
                                                                className={'select form-control align-self-stretch'}
                                                                {...field}
                                                                value={field.value ?? ''}
                                                            >
                                                                <option value={''}>Select...</option>
                                                                {CreditTypes.map((ct) => (
                                                                    <option key={ct} value={ct}>
                                                                        {ct}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        )}
                                                    />
                                                    {errors?.typeOfCredits2?.message && (
                                                        <Label className={'text-danger'}>
                                                            {errors.typeOfCredits2.message}
                                                        </Label>
                                                    )}
                                                </FlexFormGroup>
                                                <FlexFormGroup>
                                                    <Label>Total Program Tuition and Fees - Resident</Label>
                                                    <Controller
                                                        name="totalFeesResident2"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Input
                                                                {...field}
                                                                type={'number'}
                                                                value={field.value ?? ''}
                                                                onChange={(e) => field.onChange(e.target.value === '' ? undefined : +e.target.value)}
                                                            />
                                                        )}
                                                    />
                                                    {errors?.totalFeesResident2?.message && (
                                                        <Label className={'text-danger'}>
                                                            {errors.totalFeesResident2.message}
                                                        </Label>
                                                    )}
                                                </FlexFormGroup>
                                                <FlexFormGroup>
                                                    <Label>Total Program Tuition and Fees - Non-Resident</Label>
                                                    <Controller
                                                        name="totalFeesNonResident2"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Input
                                                                {...field}
                                                                type={'number'}
                                                                value={field.value ?? ''}
                                                                onChange={(e) => field.onChange(e.target.value === '' ? undefined : +e.target.value)}
                                                            />
                                                        )}
                                                    />
                                                    {errors?.totalFeesNonResident2?.message && (
                                                        <Label className={'text-danger'}>
                                                            {errors.totalFeesNonResident2.message}
                                                        </Label>
                                                    )}
                                                </FlexFormGroup>
                                            </Col>
                                        </Row>
                                        <Row className={'my-2'}>
                                            <Col>
                                                <h4 className={'card-title'}>Program Budget</h4>
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col>
                                                <FlexFormGroup>
                                                    <Label>
                                                        {`Program's fiscal year begins on (month/date)`}
                                                        <span className={'text-danger ms-1'}>*</span>
                                                    </Label>
                                                    <Row>
                                                        <Col xs={12} md={6} xl={3}>
                                                            <Controller
                                                                name="programFiscalYearBeginsOnMonth"
                                                                control={control}
                                                                render={({ field }) => (
                                                                    <select
                                                                        className={'select form-control align-self-stretch'}
                                                                        {...field}
                                                                        onChange={(e) => field.onChange(+e.target.value)}
                                                                    >
                                                                        <option value={''}>Select...</option>
                                                                        {Object.keys(Months)
                                                                            .filter((m) => isNaN(+m))
                                                                            .map((month) => (
                                                                                <option
                                                                                    key={Months[month]}
                                                                                    value={Months[month]}
                                                                                >
                                                                                    {month}
                                                                                </option>
                                                                            ))}
                                                                    </select>
                                                                )}
                                                            />
                                                            <div>
                                                                {errors?.programFiscalYearBeginsOnMonth?.message && (
                                                                    <Label className={'text-danger'}>
                                                                        {errors.programFiscalYearBeginsOnMonth.message}
                                                                    </Label>
                                                                )}
                                                            </div>
                                                        </Col>
                                                        <Col xs={12} md={6} xl={3}>
                                                            <Controller
                                                                name="programFiscalYearBeginsOnDate"
                                                                control={control}
                                                                render={({ field }) => (
                                                                    <select
                                                                        className={'select form-control align-self-stretch'}
                                                                        {...field}
                                                                        onChange={(e) => field.onChange(+e.target.value)}
                                                                    >
                                                                        <option value={''}>Select...</option>
                                                                        {Days.map((day) => (
                                                                            <option key={day} value={day}>
                                                                                {day}
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                )}
                                                            />
                                                            <div>
                                                                {errors?.programFiscalYearBeginsOnDate?.message && (
                                                                    <Label className={'text-danger'}>
                                                                        {errors.programFiscalYearBeginsOnDate.message}
                                                                    </Label>
                                                                )}
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                </FlexFormGroup>
                                            </Col>
                                        </Row>
                                        {professionExpenditures && professionExpenditures.length > 0 && (
                                            <Row>
                                                <Col className={'my-2'} xs={12}>
                                                    <Label>{`Indicate the Program's actual expenditures for:`}</Label>
                                                </Col>
                                                <Col className={'d-flex flex-column'}>
                                                    <h5 className={'card-subtitle mb-2'}>
                                                        {`Current Fiscal Year to Date`}
                                                    </h5>
                                                    {professionExpenditures.map((exp, i) => (
                                                        <FlexFormGroup key={`exp-${i}`}>
                                                            <input type="hidden" {...register(`programInformationDetail.${i}.id` as any)} defaultValue={findExpenditureInDetail(exp.title)?.id || 0} />
                                                            <input type="hidden" {...register(`programInformationDetail.${i}.programInfoId` as any)} defaultValue={programInformation?.programInfoId || 0} />
                                                            <input type="hidden" {...register(`programInformationDetail.${i}.title` as any)} defaultValue={findExpenditureInDetail(exp.title)?.title || exp.title} />
                                                            <Label>
                                                                {exp.title}
                                                                <span className={'text-danger ms-1'}>*</span>
                                                            </Label>
                                                            <InputGroup>
                                                                <InputGroupText>$</InputGroupText>
                                                                <Controller
                                                                    name={`programInformationDetail.${i}.expenditure` as any}
                                                                    control={control}
                                                                    render={({ field }) => (
                                                                        <Input
                                                                            {...field}
                                                                            type={'number'}
                                                                            value={field.value ?? ''}
                                                                            onChange={(e) => field.onChange(e.target.value === '' ? undefined : +e.target.value)}
                                                                        />
                                                                    )}
                                                                />
                                                            </InputGroup>
                                                            {errors?.programInformationDetail &&
                                                                errors.programInformationDetail[i]?.expenditure
                                                                    ?.message && (
                                                                    <Label className={'text-danger'}>
                                                                        {
                                                                            errors.programInformationDetail[i]
                                                                                ?.expenditure?.message
                                                                        }
                                                                    </Label>
                                                                )}
                                                        </FlexFormGroup>
                                                    ))}
                                                    <FormGroup>
                                                        <Label>Total</Label>
                                                        {getExpTotal() && (
                                                            <span className={'ms-2'}>
                                                                {getExpTotal()?.toLocaleString('us', {
                                                                    style: 'currency',
                                                                    currency: 'USD',
                                                                })}
                                                            </span>
                                                        )}
                                                        <input type="hidden" {...register('totalExpenditures')} />
                                                    </FormGroup>
                                                </Col>
                                                <Col className={'d-flex flex-column'}>
                                                    <h5 className={'card-subtitle mb-2'}>Previous Fiscal Year</h5>
                                                    {professionExpenditures.map((exp, i) => (
                                                        <FlexFormGroup key={`prevexp-${i}`}>
                                                            <Label>
                                                                {exp.title}
                                                                <span className={'text-danger ms-1'}>*</span>
                                                            </Label>
                                                            <InputGroup>
                                                                <InputGroupText>$</InputGroupText>
                                                                <Controller
                                                                    name={`programInformationDetail.${i}.previousExpenditure` as any}
                                                                    control={control}
                                                                    render={({ field }) => (
                                                                        <Input
                                                                            {...field}
                                                                            type={'number'}
                                                                            value={field.value ?? ''}
                                                                            onChange={(e) => field.onChange(e.target.value === '' ? undefined : +e.target.value)}
                                                                        />
                                                                    )}
                                                                />
                                                            </InputGroup>
                                                            {errors?.programInformationDetail &&
                                                                errors.programInformationDetail[i]?.previousExpenditure
                                                                    ?.message && (
                                                                    <Label className={'text-danger'}>
                                                                        {
                                                                            errors.programInformationDetail[i]
                                                                                ?.previousExpenditure?.message
                                                                        }
                                                                    </Label>
                                                                )}
                                                        </FlexFormGroup>
                                                    ))}
                                                    <FormGroup>
                                                        <Label>Total</Label>
                                                        {getPrevExpTotal() && (
                                                            <span className={'ms-2'}>
                                                                {getPrevExpTotal()?.toLocaleString('us', {
                                                                    style: 'currency',
                                                                    currency: 'USD',
                                                                })}
                                                            </span>
                                                        )}
                                                        <input type="hidden" {...register('totalPreviousExpenditures')} />
                                                    </FormGroup>
                                                </Col>
                                            </Row>
                                        )}
                                        <Row>
                                            <Col>
                                                <FlexFormGroup>
                                                    <Label>
                                                        {`Is the budget sufficient to ensure achievement of the
                                                        Program's goal and outcomes?`}
                                                        <span className={'text-danger ms-1'}>*</span>
                                                    </Label>
                                                    <Controller
                                                        name="isBudgetSufficient"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <div className={'d-flex'}>
                                                                <div className={'custom-control custom-radio me-3'}>
                                                                    <input
                                                                        type="radio"
                                                                        id={'isBudgetSufficientYes'}
                                                                        className="custom-control-input"
                                                                        name={field.name}
                                                                        checked={field.value === true}
                                                                        value="true"
                                                                        onChange={() => field.onChange(true)}
                                                                    />
                                                                    <label
                                                                        htmlFor={'isBudgetSufficientYes'}
                                                                        className="custom-control-label"
                                                                    >{`Yes`}</label>
                                                                </div>
                                                                <div className={'custom-control custom-radio'}>
                                                                    <input
                                                                        type="radio"
                                                                        id={'isBudgetSufficientNo'}
                                                                        className="custom-control-input"
                                                                        name={field.name}
                                                                        checked={field.value === false}
                                                                        value="false"
                                                                        onChange={() => field.onChange(false)}
                                                                    />
                                                                    <label
                                                                        htmlFor={'isBudgetSufficientNo'}
                                                                        className="custom-control-label"
                                                                    >{`No`}</label>
                                                                </div>
                                                            </div>
                                                        )}
                                                    />
                                                    <div>
                                                        {errors?.isBudgetSufficient?.message && (
                                                            <Label className={'text-danger'}>
                                                                {errors.isBudgetSufficient.message}
                                                            </Label>
                                                        )}
                                                    </div>
                                                </FlexFormGroup>
                                            </Col>
                                        </Row>
                                        {watchBudget === false && (
                                            <Row>
                                                <Col>
                                                    <FlexFormGroup>
                                                        <Label>
                                                            If budget is not sufficient, provide analysis and action
                                                            plan
                                                        </Label>
                                                        <Row>
                                                            <Col xs={12} md={6}>
                                                                <Label>Detailed Analysis of Insufficient Budget</Label>
                                                                <Controller
                                                                    name="detailedAnalysisOfBudget"
                                                                    control={control}
                                                                    render={({ field }) => (
                                                                        <Input {...field} value={field.value ?? ''} rows={10} type={'textarea'} />
                                                                    )}
                                                                />
                                                                <div>
                                                                    {errors?.detailedAnalysisOfBudget?.message && (
                                                                        <Label className={'text-danger'}>
                                                                            {errors.detailedAnalysisOfBudget.message}
                                                                        </Label>
                                                                    )}
                                                                </div>
                                                            </Col>
                                                            <Col xs={12} md={6}>
                                                                <Label>Action Plan for Insufficent Budget</Label>
                                                                <Controller
                                                                    name="actionPlanForBudget"
                                                                    control={control}
                                                                    render={({ field }) => (
                                                                        <Input {...field} value={field.value ?? ''} rows={10} type={'textarea'} />
                                                                    )}
                                                                />
                                                                <div>
                                                                    {errors?.actionPlanForBudget?.message && (
                                                                        <Label className={'text-danger'}>
                                                                            {errors.actionPlanForBudget.message}
                                                                        </Label>
                                                                    )}
                                                                </div>
                                                            </Col>
                                                        </Row>
                                                    </FlexFormGroup>
                                                </Col>
                                            </Row>
                                        )}
                                        <Row className={'mt-2'}>
                                            <Col>
                                                <h4 className={'card-title'}>Program Satellites</h4>
                                            </Col>
                                        </Row>
                                        {props.canEdit && (
                                            <Row>
                                                <Col>
                                                    <Button color="primary" onClick={() => newSatellite()}>
                                                        <i className={'mdi mdi-plus'} />
                                                        <span className={'ms-1'}>Add Satellite Campus</span>
                                                    </Button>
                                                </Col>
                                            </Row>
                                        )}
                                        <Row className={'mt-3'}>
                                            <Col
                                                className={`d-flex flex-wrap ${
                                                    satellites.length > 1 ? `justify-content-around` : ``
                                                }`}
                                            >
                                                {satellites &&
                                                    satellites.map((sat) => (
                                                        <Card
                                                            className={'d-flex'}
                                                            style={{ flexBasis: 'calc(33% - 10px)' }}
                                                            key={sat.satelliteId}
                                                        >
                                                            <CardBody className={'d-flex justify-content-between'}>
                                                                <div>
                                                                    <h5 className={'card-title'}>{sat.title}</h5>
                                                                    <h6 className={'card-subtitle text-muted'}>
                                                                        {`${sat.city}, ${sat.state} ${sat.postalCode}`}
                                                                    </h6>
                                                                </div>
                                                                <Button
                                                                    color="info"
                                                                    onClick={() => selectSatellite(sat)}
                                                                >
                                                                    <i className={'remixicon-pencil-line'}></i>
                                                                </Button>
                                                            </CardBody>
                                                        </Card>
                                                    ))}
                                            </Col>
                                        </Row>
                                    </CardBody>
                                    {props.canEdit && (
                                        <CardFooter className={'d-flex justify-content-between'}>
                                            <Button
                                                type={'button'}
                                                color={'warning'}
                                                onClick={() => props.onNavigate(AnnualReportNavigationDirection.Back)}
                                            >
                                                <i className={'mdi mdi-arrow-left'} />
                                                <span className={'me-1'}>{`Previous`}</span>
                                            </Button>

                                            <Button
                                                disabled={formSubmitting}
                                                type={'button'}
                                                color={'primary'}
                                                onClick={() => submitWithoutValidation()}
                                            >
                                                <i className={'mdi mdi-content-save'} />
                                                <span className={'ms-1'}>{`Save`}</span>
                                            </Button>

                                            <Button
                                                disabled={formSubmitting}
                                                type={'submit'}
                                                color={'info'}
                                                onClick={() =>
                                                    setSubmitDirection(AnnualReportNavigationDirection.Forward)
                                                }
                                            >
                                                <span className={'me-1'}>{`Save & Continue`}</span>
                                                <i className={'mdi mdi-arrow-right'} />
                                            </Button>
                                        </CardFooter>
                                    )}
                                </Card>
                            </Form>

                            <Modal isOpen={showModal} toggle={toggleModal} size={'lg'} centered={true}>
                                <ModalHeader toggle={toggleModal}>{`${
                                    selectedSatellite ? `Edit` : `Add`
                                } Satellite Campus`}</ModalHeader>
                                <ModalBody>
                                    <SatelliteForm
                                        satellite={selectedSatellite}
                                        programId={props.program.programId}
                                        onSave={() => handleSatelliteSave()}
                                        onCancel={() => toggleModal()}
                                    />
                                </ModalBody>
                            </Modal>
                        </>
                    )}
                    {!programInformation && <Skeleton count={30} />}
                </Col>
            </Row>
        </>
    );
};

export default ProgramInformation;
